import { parseDataAttributes } from '../../utilities/utilities'
import type { EnabledFilter, PriceFilterType } from '@haus-storefront-react/shared-types'

export type PriceFilterDisplayMode = 'both' | 'slider' | 'inputs'

export interface PriceFilterAttributes {
  productListIdentifier: string
  enabledFilters: EnabledFilter<PriceFilterType>[]
  displayMode: PriceFilterDisplayMode
}

const DEFAULT_ENABLED_FILTERS: EnabledFilter<PriceFilterType>[] = [{ type: 'price' }]

const DEFAULT_PRICE_FILTER_ATTRIBUTES: PriceFilterAttributes = {
  productListIdentifier: 'product-list',
  enabledFilters: DEFAULT_ENABLED_FILTERS,
  displayMode: 'both',
}

const VALID_DISPLAY_MODES: PriceFilterDisplayMode[] = ['both', 'slider', 'inputs']

const isPriceFilterDisplayMode = (value: string): value is PriceFilterDisplayMode => {
  return VALID_DISPLAY_MODES.some((mode) => mode === value)
}

const toNonEmptyString = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return fallback
  }

  const trimmedValue = String(value).trim()
  return trimmedValue.length > 0 ? trimmedValue : fallback
}

const getDisplayMode = (value: unknown): PriceFilterDisplayMode => {
  if (typeof value !== 'string') {
    return DEFAULT_PRICE_FILTER_ATTRIBUTES.displayMode
  }

  return isPriceFilterDisplayMode(value) ? value : DEFAULT_PRICE_FILTER_ATTRIBUTES.displayMode
}

/**
 * Parse Elementor data attributes into typed price filter props.
 */
export function getPriceFilterAttributes(attrs: NamedNodeMap): PriceFilterAttributes {
  const base = parseDataAttributes(attrs)

  return {
    productListIdentifier: toNonEmptyString(
      base.productListIdentifier,
      DEFAULT_PRICE_FILTER_ATTRIBUTES.productListIdentifier,
    ),
    enabledFilters: DEFAULT_PRICE_FILTER_ATTRIBUTES.enabledFilters,
    displayMode: getDisplayMode(base.displayMode),
  }
}

/**
 * Return price filter props from Elementor data attributes.
 */
export default function propsFn(dataAttributes: NamedNodeMap): PriceFilterAttributes {
  return getPriceFilterAttributes(dataAttributes)
}
